import CardCommon from '@op/components/CardCommon';
import CommonTitle from '../CommonTitle';
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Segment, TabPanel } from 'tea-component';
import LinkTab from '@op/components/LinkTab';
import {
  REALTIME_CHANNEL_ACCOUNT_OPTIONS,
  REALTIME_CHANNEL_TYPE_OPTIONS,
  REALTIME_CHANNEL_METRICS_STREAM_OPTIONS,
  REALTIME_CHANNEL_METRICS_COMMON_OPTIONS,
} from '../../../constant';
import useLanguage from '@op/hooks/useLanguage';
import MetricsCard from '../../../component/MetricsCard';
import { ICommunityProps, IMetricsCardList, IRealtimeCommunityType, IRealtimeMentionsType, IRealtimeOrganizationType } from '../../../type';
import { useRealtimeCommunityStats, useRealtimeStreamCommunityStats } from '../../../hook';
import { DBO_TM } from '@op/utils/format';
import { formatDateFunc } from '@op/utils/time';
import { reviewsFormat } from '@op/utils/value';
import CommunityChart from '../../../component/CommunityChart';
import './index.scss';
import StreamCommunityChart from '../../../component/CommunityChart/StreamCommunityChart';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { getSegmentWithIcon } from '../../../utils';

export default function Community(props: ICommunityProps) {
  const { filter, onActive } = props;
  const { formatOptionsMessage, formatMessage } = useLanguage();
  const [account, setAccount] = useState<IRealtimeOrganizationType>('all');
  const [type, setType] = useState<IRealtimeCommunityType>('total');
  const [endTime, setEndTime] = useState('');

  const isToday = useMemo(() => DBO_TM.isToday(filter), [filter]);
  const { options, defaultActiveId } = useMemo(() => {
    let options = REALTIME_CHANNEL_METRICS_COMMON_OPTIONS;
    if (type === 'stream') {
      options = REALTIME_CHANNEL_METRICS_STREAM_OPTIONS;
    }
    return { options, defaultActiveId: options[0].valueKey };
  }, [type]);

  return (
    <CardCommon
      className="dbo-community-performance-cardCommon"
      contentCard
      title={
        <CommonTitle
          title={"Community Performance"}
          endTime={endTime}
          hasTitleArrow
          handleTitleOnClick={() => {
            onActive?.();
            buriedBtnPoint('RO_Community_Title');
          }}
        />
      }
    >
      <div className="dbo-community-performance-segment">
        <Segment
          className="segment-light segment-font12"
          value={type}

          onChange={(value: IRealtimeCommunityType) => {
            setType(value);
            buriedBtnPoint('RO_Community_TypeTab', value);
          }}
          options={getSegmentWithIcon(REALTIME_CHANNEL_TYPE_OPTIONS, formatMessage)}
        />
        {
          type !== 'stream' && (
            <Segment
              className="segment-light segment-font12"
              style={{ marginLeft: 10 }}
              value={account}
              onChange={(value: IRealtimeOrganizationType) => {
                setAccount(value);
                buriedBtnPoint('RO_Community_AccountTab', value);
              }}
              options={formatOptionsMessage(REALTIME_CHANNEL_ACCOUNT_OPTIONS)}
            />
          )
        }
      </div>
      <>
        {type === 'stream' && (
          <StreamCommunityItem
            key="stream"
            defaultActiveId={defaultActiveId}
            options={options}
            isToday={isToday}
            type={type}
            setEndTime={setEndTime}
            {...props}
          />
        )}
        {type !== 'stream' && (
          <CommunityItem
            key="default"
            defaultActiveId={defaultActiveId}
            options={options}
            isToday={isToday}
            type={type}
            account={account}
            setEndTime={setEndTime}
            {...props}
          />
        )}
      </>
    </CardCommon>
  );
}

function CommunityItem({
  defaultActiveId,
  options,
  gameInfo,
  filter,
  deadline,
  isToday,
  type,
  account = 'all',
  setEndTime,
}: {
  defaultActiveId: IRealtimeMentionsType;
  options: IMetricsCardList[];
  gameInfo: any;
  filter?: any;
  wholeGameInfo?: any;
  deadline: string;
  isToday: boolean;
  type: IRealtimeCommunityType;
  account?: IRealtimeOrganizationType;
  setEndTime: (val: string) => void;
}) {
  const [byType, setByType] = useState<string>('');
  const [activeTabPanel, setActiveTabPanel] = useState<IRealtimeMentionsType>(defaultActiveId);

  useEffect(() => {
    const { dropdown } = options.find((val) => val.valueKey === activeTabPanel) || {};
    setByType(dropdown?.[0]?.value || '');
  }, [activeTabPanel]);

  const filterParams = useMemo(() => {
    if (!byType) {
      return {};
    }
    let extraFilter = {};
    if (type === 'video') {
      extraFilter = {
        review_types: ["posts_video", "sub_video"]
      };
    }
    if (type === 'posts') {
      extraFilter = {
        review_types: ["posts_text", "sub_text"]
      };
    }

    if (isToday) {
      return {
        ...filter,
        date_type: 'hourly',
        need_link: true,
        need_many_hour_ago: isToday,
        many_hour_ago: deadline === 'last_hour' ? 1 : 0,
        organization: account === 'all' ? '' : account,
        by_type: byType,
        metrics: [activeTabPanel],
        ...extraFilter
      };
    }
    return {
      ...filter,
      ...gameInfo,
      date_type: 'hourly',
      organization: account === 'all' ? '' : account,
      by_type: byType,
      metrics: [activeTabPanel],
      ...extraFilter
    };
  }, [filter, gameInfo, deadline, isToday, type, account, activeTabPanel, byType]);

  const { data, chartData, loading, refetch } = useRealtimeCommunityStats({
    filter: filterParams,
    extraData: {
      metricsKey: options.map((val) => ({
        ...val,
        value: val.valueKey
      }))
    },
  });

  const { formatMessage } = useLanguage();

  useEffect(() => {
    if (data?.new_end_date) {
      setEndTime(data.new_end_date);
    }
  }, [data]);

  return (
    <LinkTab
      tabs={options.map((item, index) => {
        const { formatValue = reviewsFormat, title: itemTitle } = item;
        const title = typeof itemTitle === 'string' ? formatMessage({ id: itemTitle }) : itemTitle;
        return {
          label: (
            <CardCommon
              // loading={loading}
              key={index}
              loadingHeight={160}
              contentCard
            >
              <MetricsCard
                data={data}
                tabItem={{
                  ...item,
                  title,
                  formatValue,
                  formatDate: (value: string) => formatDateFunc(filter?.date_type || 'hourly')(value)
                }}
              />
            </CardCommon>
          ),
          id: item.valueKey,
        };
      })}
      activeId={activeTabPanel}
      onActive={(tabItem: any) => {
        if (isToday) {
          refetch(true);
        }
        setActiveTabPanel(tabItem.id);
        buriedBtnPoint('RO_Community_LinkTab', tabItem.id);
      }}
    >
      {options.map((item) => {
        return (
          <TabPanel key={item.valueKey} id={item.valueKey}>
            <CommunityChart
              gameInfo={gameInfo}
              filter={filterParams}
              chartData={chartData}
              loading={loading}
              byType={byType}
              setByType={setByType}
              {...item}
            />
          </TabPanel>
        );
      })}
    </LinkTab>
  );
}

function StreamCommunityItem({
  defaultActiveId,
  options,
  gameInfo,
  filter,
  deadline,
  isToday,
  type,
  setEndTime
}: {
  defaultActiveId: IRealtimeMentionsType;
  options: any[];
  gameInfo: any;
  filter?: any;
  wholeGameInfo?: any;
  deadline: string;
  isToday: boolean;
  type: IRealtimeCommunityType;
  setEndTime: (val: string) => void;
}) {
  const [activeTabPanel, setActiveTabPanel] = useState<IRealtimeMentionsType>(defaultActiveId);
  const filterParams = useMemo(() => {
    if (isToday) {
      return {
        ...filter,
        date_type: 'hourly',
        need_link: true,
        need_many_hour_ago: isToday,
        many_hour_ago: deadline === 'last_hour' ? 1 : 0,
      };
    }
    return {
      ...filter,
      ...gameInfo,
      need_link: true,
      date_type: 'hourly',
    };
  }, [filter, gameInfo, deadline, isToday]);

  const { data, loading, refetch } = useRealtimeStreamCommunityStats({
    filter: filterParams,
    extraData: {
      metricsKey: options.map((val) => ({
        ...val,
        value: val.valueKey
      }))
    }
  });
  const { formatMessage } = useLanguage();

  const { metricsCardData, time, oldTime } = useMemo(() => {
    const { listData, timeline_of_end_time } = data || {};
    if (timeline_of_end_time) {
      setEndTime(timeline_of_end_time);
    }
    const { topList } = listData || {};
    return {
      ...data,
      ...listData,
      metricsCardData: topList?.find((val: { name: string; }) => val.name === 'Total') || {}
    };
  }, [data]);

  return (
    <LinkTab
      tabs={options.map((item, index) => {
        const { formatValue = reviewsFormat, title: itemTitle } = item;
        const title = typeof itemTitle === 'string' ? formatMessage({ id: itemTitle }) : itemTitle;
        return {
          label: (
            <CardCommon
              loading={loading}
              key={index}
              loadingHeight={160}
              contentCard
            >
              <MetricsCard
                data={{
                  ...metricsCardData,
                  time,
                  oldTime
                }}
                tabItem={{
                  ...item,
                  title,
                  formatValue,
                  formatDate: (value: string) => formatDateFunc(filter?.date_type || 'hourly')(value)
                }}
              />
            </CardCommon>
          ),
          id: item.valueKey,
        };
      })}
      activeId={activeTabPanel}
      onActive={(tabItem: any) => {
        setActiveTabPanel(tabItem.id);
        refetch(true);
      }}
    >
      {options.map((item, index) => {
        return (
          <TabPanel key={item.valueKey + index} id={item.valueKey}>
            <StreamCommunityChart
              {...item}
              filter={filterParams}
              loading={loading}
              chartData={data}
            />
          </TabPanel>
        );
      })}
    </LinkTab>
  );
}
